import {FC} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {errorHandler} from '@utilities/helpers.ts'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {DeleteModal} from '@/components/commons/delete-modal/DeleteModal'
import {TreatmentRequestSmall} from '@/features/treatment-requests/types.ts'
import {StyledSuccessToastContent} from '@/features/treatment-requests/components/delete-treatment-request-modal/style.ts'
import {treatmentRequestStatusToLabel} from '@/features/treatment-requests/utils.ts'
import {useDeleteTreatmentRequest} from '@/features/treatment-requests/services/useDeleteTreatmentRequest'

interface DeleteTreatmentRequestModalProps {
    onClose: () => void
    onSuccess?: () => void
    treatmentRequest: TreatmentRequestSmall
}
export const DeleteTreatmentRequestModal: FC<DeleteTreatmentRequestModalProps> = ({
    treatmentRequest,
    onClose,
    onSuccess
}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const deleteMutation = useDeleteTreatmentRequest(treatmentRequest.id, {
        onError: errorHandler,
        onSuccess: async () => {
            onSuccess?.()
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, treatmentRequest.patientId]})
            toast.success(
                <StyledSuccessToastContent>
                    <h3>{t('treatment_requests:success_delete_title', {count: 1})}</h3>
                    <p>{t('commons:patient_fullname_value', {fullName: treatmentRequest.patientFullName})}</p>
                </StyledSuccessToastContent>
            )
            onClose()
        }
    })

    return (
        <DeleteModal
            onClose={onClose}
            deleteMutation={deleteMutation}
            canBeDeleted={treatmentRequest.status == 'in_review'}
            fullName={treatmentRequest.patientFullName}
            paragraph1={t('treatment_requests:delete_paragraph_1', {count: 1})}
            title={t('treatment_requests:delete_title')}
            deniedTitle={t('treatment_requests:cannot_delete_title')}
            deniedParagraph={t('treatment_requests:cannot_delete_paragraph', {
                status: t(treatmentRequestStatusToLabel[treatmentRequest.status], {count: 1})
            })}
        />
    )
}
